import { FC, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AcademicViewProps } from "./academic.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import MainTitle from "../../components/mainTitle";
import {
  useCourseHook,
  useDeleteCourseHook,
  useClassHook,
  useSyllabusHook,
} from "../../../hooks/useAcademicHook";
import { TCourse, TCourseStatus } from "../../../apis/academic/academic.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import { useObepListHook } from "../../../hooks/useObepHook";
import { logger, pages } from "../../../utils/common";
import ManageCourseDialog from "./manageCourseDialog";
import ClassListView from "./classList.view";
import GradeLevelsView from "./gradeLevels.view";
import SyllabusListView from "./syllabusList.view";
import SyllabusAssignmentView from "./syllabusAssignment.view";

const AcademicView: FC<AcademicViewProps> = ({ section }) => {
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();
  const { data, isLoading, error } = useCourseHook();
  const { data: classes } = useClassHook();
  const { data: syllabi } = useSyllabusHook();
  const deleteCourseMutation = useDeleteCourseHook();
  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");
  const { data: obepRows } = useObepListHook({ page: 1, limit: 100 });
  const obepNameById = useMemo(() => {
    const m = new Map<string, string>();
    obepRows?.forEach((r) => m.set(r.obepId, r.obepName));
    return m;
  }, [obepRows]);

  const [statusFilter, setStatusFilter] = useState<TCourseStatus | "all">(
    "all",
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<TCourse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [manageCourseDialogOpen, setManageCourseDialogOpen] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState<TCourse | null>(null);

  const filteredCourses = useMemo(() => {
    if (!data) return [];

    return data.filter((course) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        course.name.toLowerCase().includes(q) ||
        course.obepId.toLowerCase().includes(q) ||
        (course.startGrade?.toLowerCase().includes(q) ?? false) ||
        (course.endGrade?.toLowerCase().includes(q) ?? false) ||
        (course.description?.toLowerCase().includes(q) ?? false);

      const matchesStatus =
        statusFilter === "all" || course.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchQuery, statusFilter]);

  const getStatusChip = (status: TCourseStatus) => {
    const statusConfig = {
      active: {
        label: "Active",
        color: "success" as const,
        icon: <CheckCircle fontSize="small" />,
      },
      inactive: {
        label: "Inactive",
        color: "default" as const,
        icon: <Cancel fontSize="small" />,
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] ?? statusConfig.active;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const formatGradeRange = (course: TCourse) => {
    if (course.startGrade && course.endGrade) {
      return `${course.startGrade}–${course.endGrade}`;
    }
    if (course.startGrade) return `${course.startGrade}+`;
    if (course.endGrade) return `≤${course.endGrade}`;
    return "—";
  };

  const handleAddCourse = () => {
    setCourseToEdit(null);
    setManageCourseDialogOpen(true);
  };

  const handleEditCourse = (course: TCourse) => {
    setCourseToEdit(course);
    setManageCourseDialogOpen(true);
  };

  const handleCloseManageCourseDialog = () => {
    setManageCourseDialogOpen(false);
    setCourseToEdit(null);
  };

  const handleDeleteCourse = (course: TCourse) => {
    setCourseToDelete(course);
    setDeleteError(null);

    // Check dependencies
    const dependentClasses =
      classes?.filter((cls) => cls.courseId === course.id) || [];
    const dependentSyllabi =
      syllabi?.filter((s) => s.courseId === course.id) || [];

    if (dependentClasses.length > 0 || dependentSyllabi.length > 0) {
      const errors: string[] = [];
      if (dependentClasses.length > 0) {
        errors.push(
          `${dependentClasses.length} class(es) are using this course`,
        );
      }
      if (dependentSyllabi.length > 0) {
        errors.push(
          `${dependentSyllabi.length} syllabus/syllabi are assigned to this course`,
        );
      }
      setDeleteError(
        `Cannot delete course. ${errors.join(". ")}. Please remove these dependencies first.`,
      );
    }

    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;

    if (deleteError) {
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
      setDeleteError(null);
      return;
    }

    try {
      await deleteCourseMutation.mutateAsync(courseToDelete.id);
      logger.show_success("Course deleted successfully!");
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
      setDeleteError(null);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const body = error.response?.data as { message?: string } | undefined;
        logger.show_error(
          body?.message ??
          "This course is still in use. Remove dependent classes or syllabi first, then try again.",
        );
        return;
      }
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete course. Please try again.",
      );
    }
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Box p={3} data-testid="academic-view">
          <MainTitle title="Academic Structure" />
          <Alert severity="info" data-testid="academic-no-institute">
            Please select an institute from the dashboard to view courses.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.academics} />

      <Grid container spacing={3} data-testid="academic-view">
        <Grid item xs={12}>
          {section === "courses" && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
              data-testid="academic-header"
            >
              <MainTitle title="Courses" />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCourse}
                size="large"
                data-testid="add-course-button"
              >
                Add Course
              </Button>
            </Box>
          )}
          {(section === "syllabus" || section === "syllabus-assignment") && (
            <Box
              sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}
              data-testid="syllabus-inner-tabs-container"
            >
              <Tabs
                value={section === "syllabus" ? 0 : 1}
                onChange={(_, newValue) =>
                  navigate(
                    newValue === 0
                      ? `${pages.academics.path}/syllabus`
                      : `${pages.academics.path}/syllabus/assignment`,
                  )
                }
                data-testid="syllabus-inner-tabs"
              >
                <Tab label="Syllabus list" data-testid="syllabus-list-inner-tab" />
                <Tab
                  label="Syllabus assignment"
                  data-testid="syllabus-assignment-inner-tab"
                />
              </Tabs>
            </Box>
          )}
        </Grid>

        {section === "courses" && (
          <>
            {isLoading ? (
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="400px"
                  data-testid="courses-loading"
                >
                  <CircularProgress data-testid="courses-loading-spinner" />
                </Box>
              </Grid>
            ) : error ? (
              <Grid item xs={12}>
                <Alert severity="error" data-testid="courses-error">
                  Failed to load courses. Please try again later.
                </Alert>
              </Grid>
            ) : (
              <>
                {/* Search and Filters */}
                <Grid item xs={12}>
                  <Card data-testid="course-filters-card">
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <ControlledTextField
                            control={control as any}
                            name="search"
                            id="course-search"
                            label="Search"
                            isGridItem={false}
                            fullWidth
                            placeholder="Search by name, OBEP, grade range, or description..."
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{ "data-testid": "course-search-input" }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Typography
                              variant="body2"
                              sx={{ alignSelf: "center", minWidth: "60px" }}
                            >
                              Status:
                            </Typography>
                            {(["all", "active", "inactive"] as const).map(
                              (status) => (
                                <Chip
                                  key={status}
                                  label={
                                    status === "all"
                                      ? "All"
                                      : status.charAt(0).toUpperCase() +
                                      status.slice(1)
                                  }
                                  onClick={() => setStatusFilter(status)}
                                  color={
                                    statusFilter === status
                                      ? "primary"
                                      : "default"
                                  }
                                  variant={
                                    statusFilter === status
                                      ? "filled"
                                      : "outlined"
                                  }
                                  size="small"
                                  data-testid={`course-status-filter-${status}`}
                                />
                              ),
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Course List */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    data-testid="courses-count"
                  >
                    Courses ({filteredCourses.length})
                  </Typography>
                </Grid>

                {filteredCourses.length === 0 ? (
                  <Grid item xs={12}>
                    <Card data-testid="course-empty-state">
                      <CardContent>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          minHeight="200px"
                          gap={2}
                        >
                          <SchoolIcon
                            sx={{ fontSize: 64, color: "text.secondary" }}
                          />
                          <Typography variant="h6" color="text.secondary">
                            {data?.length === 0
                              ? "No courses configured"
                              : "No courses match your search criteria"}
                          </Typography>
                          {data?.length === 0 && (
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Get started by adding your first course.
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddCourse}
                                data-testid="add-course-empty-button"
                              >
                                Add Course
                              </Button>
                            </>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <TableContainer
                      component={Paper}
                      data-testid="course-table-container"
                    >
                      <Table data-testid="course-table">
                        <TableHead data-testid="course-table-head">
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>OBEP</TableCell>
                            <TableCell>Grade range</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody data-testid="course-table-body">
                          {filteredCourses.map((course) => (
                            <TableRow
                              key={course.id}
                              hover
                              data-testid={`course-row-${course.id}`}
                            >
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {course.name}
                                  </Typography>
                                  {course.description && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {course.description}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {obepNameById.get(course.obepId) ??
                                    course.obepId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatGradeRange(course)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {getStatusChip(course.status)}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCourse(course)}
                                  color="primary"
                                  disabled={course.status === "inactive"}
                                  data-testid={`edit-course-${course.id}`}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteCourse(course)}
                                  color="error"
                                  data-testid={`delete-course-${course.id}`}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {section === "classes" && (
          <Grid item xs={12} data-testid="classes-tab-content">
            <ClassListView />
          </Grid>
        )}

        {section === "syllabus" && (
          <Grid item xs={12} data-testid="syllabus-tab-content">
            <SyllabusListView />
          </Grid>
        )}

        {section === "scoring-rubrics" && (
          <Grid item xs={12} data-testid="grade-levels-tab-content">
            <GradeLevelsView />
          </Grid>
        )}

        {section === "syllabus-assignment" && (
          <Grid item xs={12} data-testid="syllabus-assignment-tab-content">
            <SyllabusAssignmentView />
          </Grid>
        )}
      </Grid>

      <ManageCourseDialog
        open={manageCourseDialogOpen}
        onClose={handleCloseManageCourseDialog}
        course={courseToEdit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCourseToDelete(null);
          setDeleteError(null);
        }}
        data-testid="delete-course-dialog"
      >
        <DialogTitle data-testid="delete-course-dialog-title">
          Delete Course
        </DialogTitle>
        <DialogContent data-testid="delete-course-dialog-content">
          {deleteError ? (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="delete-course-error"
            >
              {deleteError}
            </Alert>
          ) : null}
          <DialogContentText>
            Are you sure you want to delete course "{courseToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-course-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setCourseToDelete(null);
            }}
            disabled={deleteCourseMutation.isPending}
            data-testid="cancel-delete-course-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteCourseMutation.isPending || !!deleteError}
            startIcon={
              deleteCourseMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-course-button"
          >
            {deleteCourseMutation.isPending
              ? "Deleting..."
              : deleteError
                ? "Close"
                : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AcademicView;
